import { useQuery } from '@apollo/client';
import { Drawer, Tooltip } from 'antd';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { USER_QUERY } from '../app/graphql/authQueries';
import { useReduxDialogControls } from '../common/utils/dialogUtils';
import { extractGraphqlEntity } from '../common/utils/graphqlUtils';
import { FAIcon } from '../components/adapters/fontAwesomeAdapters';
import { usePageGuard } from '../components/guards/PageGuard';
import { useResponsiveQueries } from '../components/responsive/responsiveQueries';
import { mainMenuConfig } from '../config/mainMenuConfig';
import { useMainMenuDrawerStyles } from './MainMenuDrawer.styles';

function useDrawerOpeners() {
  const addressBook = useReduxDialogControls({ id: 'account.addressBook' });
  const invoice = useReduxDialogControls({ id: 'account.invoice' });
  const statement = useReduxDialogControls({ id: 'account.statement' });
  const reports = useReduxDialogControls({ id: 'account.reports' });
  const templates = useReduxDialogControls({
    id: 'account.templates',
  });
  return {
    'account.addressBook': addressBook,
    'account.invoice': invoice,
    'account.statement': statement,
    'account.reports': reports,
    'account.templates': templates,
  };
}

function filterBlocksByUser(blocks, user) {
  return blocks.filter(block => !block.userGate || block.userGate(user));
}

function MenuItem({
  item,
  drawerOpeners,
  closeMainDrawer,
  forceDisabled,
  disabledTooltipId,
  afterGroup,
}) {
  const { styles } = useMainMenuDrawerStyles();
  const { enabled: guardEnabled } = usePageGuard(item.guard);

  if (item.guard && !guardEnabled) {
    return null;
  }

  const disabled = item.disabled || forceDisabled;

  const content = <FormattedMessage id={item.textId} />;

  const itemClassName = classNames(styles.item, {
    [styles.itemAfterGroup]: afterGroup,
  });

  if (disabled) {
    const disabledSpan = (
      <span className={classNames(itemClassName, styles.itemDisabled)}>
        {content}
      </span>
    );
    if (disabledTooltipId) {
      return (
        <Tooltip
          styles={{ root: { maxWidth: 160 } }}
          title={<FormattedMessage id={disabledTooltipId} />}
        >
          {disabledSpan}
        </Tooltip>
      );
    }
    return disabledSpan;
  }

  if (item.to) {
    return (
      <Link to={item.to} className={itemClassName} onClick={closeMainDrawer}>
        {content}
      </Link>
    );
  }

  if (item.href) {
    return (
      <a
        className={itemClassName}
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={closeMainDrawer}
      >
        {content}
      </a>
    );
  }

  if (item.drawerId) {
    const controls = drawerOpeners[item.drawerId];
    return (
      <span
        className={itemClassName}
        onClick={() => {
          controls.open();
          closeMainDrawer();
        }}
      >
        {content}
      </span>
    );
  }

  return (
    <span className={classNames(itemClassName, styles.itemDisabled)}>
      {content}
    </span>
  );
}

function MenuEntry({
  entry,
  drawerOpeners,
  closeMainDrawer,
  forceDisabled,
  disabledTooltipId,
  afterGroup,
}) {
  const { styles } = useMainMenuDrawerStyles();
  if (entry.groupTitleId) {
    return (
      <div className={styles.group}>
        <h4 className={styles.groupTitle}>
          <FormattedMessage id={entry.groupTitleId} />
        </h4>
        {(entry?.items || []).map(item => (
          <MenuItem
            key={item.textId}
            item={item}
            drawerOpeners={drawerOpeners}
            closeMainDrawer={closeMainDrawer}
            forceDisabled={forceDisabled}
            disabledTooltipId={disabledTooltipId}
          />
        ))}
      </div>
    );
  }
  return (
    <MenuItem
      item={entry}
      drawerOpeners={drawerOpeners}
      closeMainDrawer={closeMainDrawer}
      forceDisabled={forceDisabled}
      disabledTooltipId={disabledTooltipId}
      afterGroup={afterGroup}
    />
  );
}

function MenuBlock({
  block,
  drawerOpeners,
  closeMainDrawer,
  isNarrow,
  isSmall,
}) {
  const { styles } = useMainMenuDrawerStyles();
  if (block.groupTitleId) {
    return (
      <MenuEntry
        entry={block}
        drawerOpeners={drawerOpeners}
        closeMainDrawer={closeMainDrawer}
      />
    );
  }
  const forceDisabled = Boolean(block.disabledOnNarrow && isNarrow);
  const disabledTooltipId = forceDisabled ? block.disabledTooltipId : undefined;
  return (
    <div className={styles.section}>
      <h3
        className={classNames(styles.sectionTitle, {
          [styles.sectionTitleSmall]: isSmall,
        })}
      >
        <FormattedMessage id={block.titleId} />
      </h3>
      {block.entries.map((entry, idx) => (
        <MenuEntry
          key={entry.textId || entry.groupTitleId}
          entry={entry}
          drawerOpeners={drawerOpeners}
          closeMainDrawer={closeMainDrawer}
          forceDisabled={forceDisabled}
          disabledTooltipId={disabledTooltipId}
          afterGroup={Boolean(idx > 0 && block.entries[idx - 1].groupTitleId)}
        />
      ))}
    </div>
  );
}

function MenuColumn({
  blocks,
  drawerOpeners,
  closeMainDrawer,
  user,
  isNarrow,
  isSmall,
}) {
  const { styles } = useMainMenuDrawerStyles();
  const visibleBlocks = filterBlocksByUser(blocks, user);
  if (visibleBlocks.length === 0) return null;
  return (
    <div className={styles.column}>
      {visibleBlocks.map(block => (
        <MenuBlock
          key={block.titleId || block.groupTitleId}
          block={block}
          drawerOpeners={drawerOpeners}
          closeMainDrawer={closeMainDrawer}
          isNarrow={isNarrow}
          isSmall={isSmall}
        />
      ))}
    </div>
  );
}

function MenuRow({
  row,
  drawerOpeners,
  closeMainDrawer,
  user,
  isNarrow,
  isSmall,
}) {
  const { styles } = useMainMenuDrawerStyles();
  return (
    <div className={styles.row}>
      {row.titleId && (
        <h3
          className={classNames(styles.sectionTitle, styles.rowTitle, {
            [styles.sectionTitleSmall]: isSmall,
          })}
        >
          <FormattedMessage id={row.titleId} />
        </h3>
      )}
      <div className={styles.columns}>
        {row.columns.map((blocks, idx) => (
          <MenuColumn
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            blocks={blocks}
            drawerOpeners={drawerOpeners}
            closeMainDrawer={closeMainDrawer}
            user={user}
            isNarrow={isNarrow}
            isSmall={isSmall}
          />
        ))}
      </div>
    </div>
  );
}

export default function MainMenuDrawer() {
  const { isOpen, close } = useReduxDialogControls({ id: 'mainMenu' });
  const media = useResponsiveQueries();
  const { styles } = useMainMenuDrawerStyles();
  const isSmall = media.sm || media.xs;
  const isNarrow = isSmall || media.md; // width < 870px
  const drawerOpeners = useDrawerOpeners();
  const { data, loading } = useQuery(USER_QUERY);
  const user = data && extractGraphqlEntity(data);

  const closeMainDrawer = () => close();

  const title = (
    <div className={styles.title}>
      <FAIcon
        icon="times-circle"
        className={classNames(styles.close, 'Clickable icon-16')}
        onClick={closeMainDrawer}
      />
      <span>
        <FormattedMessage id="mainMenu.title" />
      </span>
    </div>
  );

  if (loading || !user) {
    return null;
  }

  return (
    <Drawer
      open={isOpen}
      onClose={closeMainDrawer}
      placement="right"
      closable={false}
      mask
      maskClosable
      title={title}
      width={isSmall ? '100%' : 410}
      classNames={{ content: styles.drawer }}
      styles={{
        wrapper: { boxShadow: 'none', top: isSmall ? 0 : 68 },
        mask: { background: 'none' },
      }}
    >
      {mainMenuConfig.rows.map((row, idx) => (
        <MenuRow
          // eslint-disable-next-line react/no-array-index-key
          key={row.titleId || idx}
          row={row}
          drawerOpeners={drawerOpeners}
          closeMainDrawer={closeMainDrawer}
          user={user}
          isNarrow={isNarrow}
          isSmall={isSmall}
        />
      ))}
    </Drawer>
  );
}
